import {EventEmitter, Injectable, Renderer2, RendererFactory2} from '@angular/core';
import {Observable, of} from "rxjs";
import {BackendService} from "./backend.service";
import {map, share} from "rxjs/operators";

/**
 * Service with helper functions for infobuttons
 */
@Injectable({
  providedIn: 'root'
})
export class InfoService {
  // infobuttons
  static readonly INFOBUTTON_MARKER_FILL = 'rgba(225,0,225,0.2)';
  static readonly INFOBUTTON_MARKER_BORDER = 'rgba(225,0,225,1)';

  /**
   * Eventemitter that is fired when any infobutton is opened.
   */
  public infoOpened: EventEmitter<string>;

  /**
   * Some kind of cache for the image data of infobutton images
   * @private
   */
  private imageData: {[key: string]: {image: string, crop?: string, marker?: string[]}};

  /**
   * Observable for the request to the backend to do this only once.
   * @private
   */
  private backendRequest: Observable<{image: string, crop?: string, marker?: string[]}>;

  /**
   * Holds the field id of the last opened info overlay.
   * @private
   */
  private lastId: string;

  /**
   * constructor
   * @param backendService
   * @param rendererFactory2
   */
  constructor(private backendService: BackendService, private rendererFactory2: RendererFactory2) {
    this.infoOpened = new EventEmitter<string>();

    // catch clicks somewhere on the document
    const renderer = this.rendererFactory2.createRenderer(null, null);
    this.initClickHandler(renderer);
  }

  /**
   * Initiate a click handler for catching clicks on the document to decide if we want to close all open help texts.
   * @param renderer
   */
  initClickHandler(renderer: Renderer2): void {
    renderer.listen("document", "click", event => {
      if(!this.lastId || !event.target) {
        // ignore this click
        return;
      }

      let elem = event.target;
      for ( ; elem && elem !== document && elem !== elem.parentNode; elem = elem.parentNode ) {
        if ( elem.matches( '[app-info-icon]' ) ) {
          // ignore clicks on (other) infobuttons
          return;
        }
      }

      // ignore race conditions. If elem is null, the parent was removed from DOM while searching for it.
      if(!elem) {
        return null;
      }

      // TODO: to optimize the closing behaviour, we could ignore clicks on the currently active formfield.
      // get the nearest formfield
      // elem = event.target;
      // for ( ; elem && elem !== document; elem = elem.parentNode ) {
      //   if ( elem.matches( '[formfield]' ) ) {
      //     break;
      //   }
      // }
      // if(elem) {
      //   // TODO: ignore the click if it belongs to another formfield
      // }

      // close all (possible) open infobuttons
      this.infoOpened.emit(null);
      // this.lastId = null;
    });
  }

  /**
   * Should be called b infobuttons when they are opened.
   * @param id
   */
  public openInfoWindow(id: string) {
    this.lastId = id;
    this.infoOpened.emit(id);
  }

  /**
   * Called if an info window is closed.
   */
  public closeInfoWindow(id: string) {
    if(this.lastId === id) {
      this.lastId = null;
    }
  }

  /**
   * Returns the information for one single infobutton image
   * @param advisor
   * @param fields
   */
  public getInfobuttonImage(advisor: string, fields: string[]): Observable<{image: string, crop?: string, marker?: string[]}> {
    if(this.imageData) {
      // case 1: Data is already here
      return of(this.findImageData(fields));
    }
    else if(this.backendRequest) {
      // case 2: Backend request is running, wait for it
      return this.backendRequest.pipe(map(() => {
        // return the data for the requested field.
        return this.findImageData(fields)
      }));
    }
    else {
      // case 3: Not data, no request running. Iniialize the request to the backend and return the mapped result.
      this.backendRequest = this.backendService.getInfobuttonImages(advisor)
        .pipe(
          share(),
          map(data => {
            // request came back from the backend, save the data and reset the observable
            this.imageData = data;
            this.backendRequest = null;
            // return the result for the initially requested field.
            return this.findImageData(fields)
          })
        );
      return this.backendRequest;
    }
  }

  private findImageData(fields: string[]): {image: string, crop?: string, marker?: string[]} {
    for(const field of fields) {
      if(this.imageData.hasOwnProperty(field)) {
        return this.imageData[field];
      }
    }
    return null;
  }

}
